import { inject, injectable } from "inversify";
import { TYPES } from "@utils/inversifyJS/types";
import type { ISoftwareVersionsRepository, SoftwareVersion, SupportedSoftware } from "@domain/repositories";

@injectable()
export class ListSoftwareVersionsService {
  @inject(TYPES.ISoftwareVersionsRepository)
  private _softwareVersionsRepository!: ISoftwareVersionsRepository;

  private normalizeVersionList(versions: string[]): string[] {
    return [...new Set(versions.map((version) => this.normalizeVersion(version)).filter(Boolean))];
  }

  private normalizeVersion(version: string): string {
    return version.trim().replace(/^(?:SN|SM)/i, "");
  }

  private sortVersionStrings(versions: string[]): string[] {
    return versions.toSorted((a, b) => b.localeCompare(a, undefined, { numeric: true }));
  }

  async getVersionsListAsync(software: SupportedSoftware = "Spectronaut"): Promise<string[]> {
    const versions = await this._softwareVersionsRepository.ListAsync(true, software);
    return this.sortVersionStrings(versions.map((version) => version.version));
  }

  async getCandidateVersionsListAsync(software: SupportedSoftware = "Spectronaut"): Promise<string[]> {
    const versions = await this.getSoftwareVersionsAsync(undefined, software);
    return Array.isArray(versions) ? this.sortVersionStrings(this.normalizeVersionList(versions.map((version) => version.version))) : [];
  }

  async getSoftwareVersionsAsync(isEnabled?: boolean, software: SupportedSoftware = "Spectronaut"): Promise<SoftwareVersion[]> {
    const versions = await this._softwareVersionsRepository.ListAsync(isEnabled, software);
    return Array.isArray(versions) ? versions.toSorted((a, b) => b.version.localeCompare(a.version, undefined, { numeric: true })) : [];
  }

  async getDefaultSpectronautVersionAsync(candidateVersions?: string[], software: SupportedSoftware = "Spectronaut"): Promise<string | undefined> {
    const candidates = new Set(this.normalizeVersionList(candidateVersions ?? (await this.getCandidateVersionsListAsync(software))));
    const versions = await this.getSoftwareVersionsAsync(true, software);
    return (
      versions.find((version) => version.isDefault && candidates.has(this.normalizeVersion(version.version)))?.version ??
      versions.find((version) => candidates.has(this.normalizeVersion(version.version)))?.version
    );
  }

  async setDefaultSpectronautVersionAsync(version: string, software: SupportedSoftware = "Spectronaut"): Promise<void> {
    await this._softwareVersionsRepository.UpdateAsync(version, { isDefault: true, enabled: true }, software);
  }

  async getSelectedSpectronautVersionsAsync(candidateVersions?: string[], software: SupportedSoftware = "Spectronaut"): Promise<string[]> {
    const candidates = candidateVersions ?? (await this.getCandidateVersionsListAsync(software));
    let versions: SoftwareVersion[];
    try {
      versions = await this.getSoftwareVersionsAsync(true, software);
    } catch {
      return candidates;
    }
    if (versions.length === 0) {
      return candidates;
    }

    const allowedVersions = new Set(this.normalizeVersionList(versions.map((version) => version.version)));
    return candidates.filter((version) => allowedVersions.has(version));
  }

  async setSelectedSpectronautVersionsAsync(candidateVersions: string[], selectedVersions: string[], software: SupportedSoftware = "Spectronaut"): Promise<void> {
    const candidates = this.normalizeVersionList(candidateVersions);
    const selected = new Set(this.normalizeVersionList(selectedVersions));

    const versions = await this.getSoftwareVersionsAsync(undefined, software);
    const currentVersions = new Map(versions.map((version) => [this.normalizeVersion(version.version), version]));
    const versionsToUpdate = candidates.filter((version) => (currentVersions.get(version)?.enabled ?? false) !== selected.has(version));

    await Promise.all(versionsToUpdate.map((version) => this._softwareVersionsRepository.UpdateAsync(version, { enabled: selected.has(version) }, software)));
  }
}
